import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Surface } from "@/components/common";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Users, UserPlus, Trash2, KeyRound, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function TeamPanel() {
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/team").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addMember = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      return toast.error(t("Udfyld navn, e-mail og en adgangskode på mindst 8 tegn"));
    }
    setSaving(true);
    try {
      await api.post("/team/members", form);
      toast.success(t("{name} er oprettet", { name: form.name }));
      setForm({ name: "", email: "", password: "" });
      setAddOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(t("Fjern {name} fra teamet?", { name: m.name }))) return;
    try { await api.delete(`/team/members/${m.id}`); toast.success(t("Bruger fjernet")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const resetPw = async () => {
    if (newPw.length < 8) return toast.error(t("Mindst 8 tegn"));
    try {
      await api.post(`/team/members/${pwFor.id}/password`, { password: newPw });
      toast.success(t("Adgangskode nulstillet"));
      setPwFor(null); setNewPw("");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return null;
  const full = data.used >= data.seats;

  return (
    <Surface testid="team-panel">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {t("Team")}</p>
        <span className="text-xs text-muted-foreground" data-testid="team-seats">{t("{used} af {seats} pladser brugt", { used: data.used, seats: data.seats })}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        {data.seats > 1
          ? t("Tilføj kollegaer, så I deler virksomhedsprofil, vidensbank og projekter.")
          : t("Jeres nuværende plan har plads til én bruger. Opgradér til Business for op til 5 brugere.")}
      </p>

      <div className="space-y-2 mb-4">
        {data.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3" data-testid={`team-member-${m.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium flex items-center gap-2 truncate">
                {m.name}
                {m.is_owner && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1.5 py-0.5"><Crown className="h-3 w-3" /> {t("Ejer")}</span>}
              </p>
              <p className="text-xs text-muted-foreground truncate">{m.email}</p>
            </div>
            {data.is_owner && !m.is_owner && (
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" title={t("Nulstil adgangskode")}
                  onClick={() => { setPwFor(m); setNewPw(""); }} data-testid={`team-reset-${m.id}`}>
                  <KeyRound className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title={t("Fjern")}
                  onClick={() => removeMember(m)} data-testid={`team-remove-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {data.is_owner && (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2" disabled={full} data-testid="team-add-btn">
              <UserPlus className="h-4 w-4" /> {full ? t("Ingen ledige pladser") : t("Tilføj bruger")}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="team-add-dialog">
            <DialogHeader><DialogTitle>{t("Tilføj teammedlem")}</DialogTitle><DialogDescription>{t("Opret en bruger med en midlertidig adgangskode, du deler med kollegaen.")}</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>{t("Navn")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" /></div>
              <div className="space-y-2"><Label>{t("E-mail")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email-input" /></div>
              <div className="space-y-2"><Label>{t("Midlertidig adgangskode")}</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password-input" placeholder={t("Mindst 8 tegn")} /><p className="text-xs text-muted-foreground">{t("Del adgangskoden med brugeren. De kan logge ind med det samme.")}</p></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>{t("Annullér")}</Button>
              <Button onClick={addMember} disabled={saving} data-testid="team-create-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{t("Opret bruger")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent data-testid="team-reset-dialog">
          <DialogHeader><DialogTitle>{pwFor ? t("Nulstil adgangskode for {name}", { name: pwFor.name }) : t("Nulstil adgangskode")}</DialogTitle><DialogDescription>{t("Angiv en ny midlertidig adgangskode, som brugeren kan logge ind med.")}</DialogDescription></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>{t("Ny adgangskode")}</Label>
            <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder={t("Mindst 8 tegn")} data-testid="team-newpw-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwFor(null)}>{t("Annullér")}</Button>
            <Button onClick={resetPw} data-testid="team-newpw-save">{t("Gem")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Surface>
  );
}
